<template>
    <calendar-loading v-if="isLoadingPreferences" />
    <div class="calendar-container" v-else>
        <h1 class="my-3 ml-4 col-sm-9" v-if="_isTvView">
            <img src="assets/daysmartvet_logo_dark.svg" width="37" />
            Schedule
        </h1>
        <b-row class="my-3">
            <b-col md="7">
                <h1 v-if="calendarApi && calendarApi.view" class="d-sm-inline">
                    <span
                        v-if="
                            calendarApi.view.view.type === 'resourceTimeGridDay'
                        "
                    >
                        {{ calendarStart.format("dddd, MMMM D, YYYY") }}
                    </span>
                    <span v-else>
                        {{ calendarStart.format("ddd, MMM D, YYYY") }} -
                        {{ calendarEnd.format("ddd, MMM D, YYYY") }}
                    </span>
                </h1>
                <actor-name
                    v-if="_timeZone"
                    :actor="$root.currentActor"
                    :useRouter="false"
                    :text="_timeZone.replace('_', '')"
                />
            </b-col>
            <b-col class="text-right" v-if="calendarApi && calendarApi.view">
                <remote-view-link
                    v-if="!_isTvView"
                    :mod="EXTERNALVIEW.SCHEDULE"
                />
                <b-button-group class="mr-4 button-nav">
                    <b-button @click="calendarApi.today()" :disabled="isToday"
                        >Today</b-button
                    >
                    <b-button
                        id="daySelector"
                        :class="{
                            active:
                                calendarApi.view.view.type ===
                                'resourceTimeGridDay'
                        }"
                        >Day</b-button
                    >
                    <b-button
                        id="weekSelector"
                        :class="{
                            active:
                                calendarApi.view.view.type !==
                                'resourceTimeGridDay'
                        }"
                        >Week</b-button
                    >
                </b-button-group>
                <b-button-group class="mx-1 button-arrow">
                    <b-button @click="calendarApi.prev()">
                        <span class="fc-icon fc-icon-chevron-left"></span>
                    </b-button>
                    <b-button @click="calendarApi.next()">
                        <span class="fc-icon fc-icon-chevron-right"></span>
                    </b-button>
                </b-button-group>
            </b-col>
        </b-row>
        <b-row no-gutters>
            <resource
                v-if="_hasAccess('schedule.member.calendar')"
                @select="setResource"
                @toggle-show-staff="toggleShowStaff"
                :resources="resources"
                :show.sync="showSidemenu"
                id="resource-list"
            />
            <b-col id="fc-schedule-appointment">
                <FullCalendar
                    id="calendar"
                    ref="fullCalendar"
                    v-bind="config"
                    :plugins="calendarPlugins"
                    :event-sources="eventSources"
                    :resources="activeResource"
                    :time-zone="timezone"
                    @select="eventCreated"
                    @eventClick="eventSelected"
                    @eventPositioned="eventRender"
                    :viewSkeletonRender="viewRender"
                    @eventDrop="eventDrop"
                    @eventResize="eventResized"
                    @eventMouseEnter="eventMouseEnter"
                    :defaultDate="now"
                    :datesRender="datesRender"
                    :defaultView="
                        $route.params.view
                            ? $route.params.view
                            : 'resourceTimeGridDay'
                    "
                    :slotDuration="slotDuration"
                    :customButtons="customButtons"
                    :selectable="!_isTvView"
                    class="vh-100"
                />
            </b-col>
        </b-row>
        <create-update ref="createUpdateWindow" :instance-id="calendarId" />
        <check-in
            ref="checkInWindow"
            :instance-id="calendarId"
            @show-loading="showLoading = $event"
        />
        <check-out ref="checkOutWindow" :instance-id="calendarId" />
        <accept ref="acceptWindow" />
        <reject ref="rejectWindow" />
        <reschedule ref="rescheduleWindow" />
        <preview
            ref="previewWindow"
            v-on="previewListener()"
            :showLoading="showLoading"
        />
        <history ref="historyWindow" />
        <undo-checkin ref="undoWindow" :instance-id="calendarId" />
        <cage-card ref="cageCardWindow" />
        <status ref="statusWindow" :instance-id="calendarId" />
        <cancel ref="cancelWindow" :instance-id="calendarId" />
        <click-drag
            ref="clickDrag"
            :instance-id="calendarId"
            @cancel="cancelDrop"
        />

        <div v-if="_.get($root, 'setting.appointment_hover') == 1">
            <hover
                v-for="event in popoverEvents"
                :target="getEventId(event.id)"
                :showLoading="showLoading"
                :key="event.id + componentKey"
                :data="event"
                v-on="previewListener()"
            />
        </div>
        <div>
            <button-calendar
                ref="daySelector"
                @change="changeDayPicker('resourceTimeGridDay', $event)"
                :date="calendarStart"
                prop-id="daySelector"
                :key="keyDaySelectorButton"
            />
            <button-calendar
                ref="weekSelector"
                @change="changeDayPicker('resourceTimeGridWeek', $event)"
                :date="calendarStart"
                :weekly="true"
                prop-id="weekSelector"
                :key="keyWeekSelectorButton"
            />
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";
import { DateTime } from "luxon";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import momentPlugin from "@fullcalendar/moment";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import interactionPlugin from "@fullcalendar/interaction";
import uuidv4 from "uuid/v4";

import CreateUpdate from "./create-update/modal.vue";
import CheckIn from "./check-in/modal.vue";
import CheckOut from "./check-out/modal.vue";
import Accept from "./online/accept.vue";
import Reject from "./online/reject.vue";
import Reschedule from "./online/reschedule.vue";
import Preview from "./preview/modal.vue";
import History from "./history.vue";
import Status from "./status.vue";
import ClickDrag from "./click-drag.vue";
import UndoCheckin from "./undo-checkin.vue";
import Cancel from "./cancel.vue";
import CageCard from "./cage-card.vue";
import ButtonCalendar from "@/module/commons/button-calendar.vue";
import fcEvent from "./component/fc-event.vue";
import Hover from "./preview/hover.vue";
import Resource from "./resource";
import ActorName from "@/components/commons/actor-name.vue";
import CSETTINGS from "@/constants/settings";
import RemoteViewLink from "@/components/commons/remote-view-link";
import CalendarLoading from "./cal-loading.vue";

export default {
    name: "CalendarAppointment",
    components: {
        FullCalendar,
        CreateUpdate,
        Preview,
        Accept,
        Reject,
        Reschedule,
        CheckIn,
        CheckOut,
        ButtonCalendar,
        History,
        Status,
        UndoCheckin,
        CageCard,
        Cancel,
        Hover,
        Resource,
        ActorName,
        ClickDrag,
        RemoteViewLink,
        CalendarLoading
    },
    data() {
        return {
            calendarPlugins: [
                dayGridPlugin,
                momentPlugin,
                interactionPlugin,
                momentTimezonePlugin,
                resourceTimeGridPlugin
            ],
            popoverEvents: [],
            newCreateDraft: false,
            draftClosed: [],
            componentKey: 0,
            calendarApi: {},
            activeResource: [],
            calendarId: uuidv4(),
            selectedResource: null,
            slotDuration: "00:20:00",
            config: {
                schedulerLicenseKey: process.env.FULLCALENDAR_LICENSE,
                allDaySlot: false,
                selectable: true,
                nowIndicator: true,
                longPressDelay: 300,
                editable: true,
                height: "parent",
                themeSystem: "bootstrap",
                slotLabelFormat: "h:mm a",

                views: {
                    week: {
                        // options apply to dayGridWeek and timeGridWeek views
                        titleFormat: "ddd MMM D, YYYY",
                        columnHeaderFormat: "ddd D" // top level of text
                    }
                },
                displayEventTime: false,
                businessHours: false,
                slotLabelInterval: { hours: 1 },
                header: false,
                resourceLabelText: "Providers"
            },
            resources: [],
            oriResources: [],
            calendarStart: moment(),
            calendarEnd: moment(),
            eventSources: [],
            generatedFc: {},
            scrollTime: "00:00:00",
            viewLoaded: 0,
            showSidemenu: true,
            showLoading: false,
            selectedEvent: {},
            EXTERNALVIEW: CSETTINGS.EXTERNAL_VIEW,
            keyDaySelectorButton: 100, // random int as key
            keyWeekSelectorButton: 200, // random int as key
            loadingAppointments: []
        };
    },
    sockets: {
        reconnect(/* socket, tryCount */) {
            this.$socket.client.emit("v-broadcast-all", {
                action: "draft-check",
                businessId: this.$root.setting.business_id,
                instance: this.calendarId
            });
        },
        "v-broadcast-all": function (event) {
            if (
                event.instance === this.calendarId &&
                event.businessId !== this.$root.setting.business_id
            ) {
                return;
            }
            let ev = {};
            switch (event.action) {
                case "update":
                    ev = this.calendarApi.getEventById(event.id);
                    if (ev) {
                        this.calendarApi.batchRendering(() => {
                            ev.setProp("editable", true);
                            ev.setDates(event.start, event.end);
                            ev.setResources([event.resourceId]);
                            ev.setProp("editable", false);
                        });
                    }
                    break;
                case "draft-add":
                    ev = this.calendarApi.getEventById(event.id);
                    if (!ev) {
                        this.calendarApi.addEvent({
                            ...event,
                            color: "#66666660",
                            status: 0,
                            editable: false
                        });
                    }
                    break;
                case "disconnect":
                case "draft-remove":
                    ev = this.calendarApi.getEventById(event.id);
                    if (ev) {
                        ev.remove();
                        this.newCreateDraft = false;
                    }
                    break;
                case "draft-check":
                    if (this.newCreateDraft) {
                        this.$socket.client.emit(
                            "v-broadcast-all",
                            this.newCreateDraft
                        );
                    }
                    break;
                case "refresh":
                    if (
                        event.instance !== this.calendarId &&
                        moment(event.params.start).isBetween(
                            this.calendarStart,
                            this.calendarApi.view.currentEnd,
                            null,
                            "[]"
                        )
                    ) {
                        this.refresh();
                    }

                    if (event.module === "treatmentBoard") {
                        this.refetch();
                    }
                    break;
                default:
                    break;
            }
        }
    },
    computed: {
        timezone() {
            return this._timeZone;
        },
        now() {
            let currentTime = DateTime.local().setZone(this.timezone);
            return currentTime.toJSDate();
        },
        isToday() {
            return moment(this.now).isBetween(
                this.calendarStart,
                this.calendarEnd
            );
        },
        customButtons() {
            return {
                dateSelector: {
                    text: "Day",
                    click() {
                        // self.$root.$emit("bv::show::popover", "daySelector");
                        // self.$root.$emit("bv::hide::popover", "weekSelector");
                    }
                },
                weekSelector: {
                    text: "Week",
                    click() {
                        // self.$root.$emit("bv::show::popover", "weekSelector");
                        // self.$root.$emit("bv::hide::popover", "daySelector");
                    }
                }
            };
        },
        isLoadingPreferences: {
            get() {
                return this.$store.state.userPreferences.isLoadingPref;
            },
            set(value) {
                return this.$store.commit("userPreferences/setLoading", value);
            }
        },
        userPreferences: {
            get() {
                return this.$store.state.userPreferences.prefs.calendar; // prettier-ignore
            },
            set(value) {
                return this.$store.commit(
                    "userPreferences/setCalendarAppointmentPref",
                    value
                );
            }
        }
    },
    destroyed() {
        // ensure that when users come from another page
        // the calendar won't be rendered before their preferences are loaded
        this.isLoadingPreferences = true;
    },
    created() {
        this.getClinicHour();
    },
    async mounted() {
        let setting = await this._getSetting();

        this.isLoadingPreferences = true;

        await this.getResources();
        let userPref = await this.getUserPreferences();

        this.isLoadingPreferences = false;

        this.calendarApi = this.$refs.fullCalendar.getApi();

        this.slotDuration = {
            minutes: setting.slot_minutes
        };

        const linkData = {
            id: this.$route.params?.id || null,
            fromLink: 1
        };
        let fromExternalLink = this.$route.params.start ? true : false;

        if (userPref) {
            this.applyUserPreferences(userPref, fromExternalLink);
        }

        if (fromExternalLink) {
            this.calendarApi.changeView(
                this.$route.params.view,
                DateTime.fromSeconds(
                    Number.parseInt(this.$route.params.start)
                ).toJSDate()
            );
            this.$router.push({ name: "appointment" });
        }

        if (!userPref && !fromExternalLink) {
            this.calendarApi.today();
        }

        this.$nextTick(() => {
            this.calendarApi.scrollToTime(this.calcScrollTime());
            $("#resource-container").empty();
            $("#resource-list")
                .removeClass("d-none")
                .appendTo("#resource-container");
            $(".calendar-container").css(
                "minHeight",
                $("#resource-container").outerHeight() + 50
            );
            this.calendarApi.updateSize();

            // re-init the component so that the tooltips would work
            this.keyDaySelectorButton++;
            this.keyWeekSelectorButton++;
            if (linkData?.id) {
                this.$refs.previewWindow.open(linkData);
            }
        });
        this.eventSources = [this.events];
        this.$socket.client.emit("v-broadcast-all", {
            action: "draft-check",
            businessId: this.$root.setting.business_id,
            instance: this.calendarId
        });
    },
    methods: {
        getEventId(id) {
            return `t-body-${id}`;
        },
        toggleShowStaff(value) {
            if (value) {
                this.userPreferences.isShowColumnStaff = true;
            } else {
                this.userPreferences.isShowColumnStaff = false;
                this.userPreferences.selectedResource = null;
            }

            // no need to call `saveUserPreferences()` here,
            // because the component 'Resource' also change the selected staff,
            // thus the `this.setResource()` will also be called.
        },
        applyUserPreferences(data, fromExternalLink = false) {
            this.userPreferences = data;
            this.showSidemenu = data?.isShowColumnStaff ?? true;

            if (data.selectedResource) {
                // check if the saved selected resource still available (might be resource has been removed)
                let resourceExists = this.resources.find(
                    r => r.id === data.selectedResource.id
                );

                if (resourceExists) {
                    this.activeResource = [data.selectedResource];
                    this.selectedResource = data.selectedResource;
                } else {
                    this.activeResource = this.resources;
                    this.selectedResource = null;
                    this.userPreferences.selectedResource = null;
                }
            }

            if (fromExternalLink) {
                return; // exit, skip default view for dates/week
            }

            if (data?.viewOptions?.day?.enabled) {
                this.calendarApi.changeView("resourceTimeGridDay", new Date());
            } else if (data?.viewOptions?.week?.enabled) {
                this.calendarApi.changeView("resourceTimeGridWeek", new Date());
            } else {
                this.calendarApi.today();
            }
        },
        /**
         * `getUserPreferences()`:
         * return the user's preferences that have been scoped to 'calendar' (not overall preferences).
         */
        async getUserPreferences() {
            if (this._isTvView) {
                this.isLoadingPreferences = false;
                return;
            }

            let res = await this.$store.dispatch("userPreferences/getPref", {
                category: "calendar"
            });

            this.isLoadingPreferences = false;
            return res;
        },
        calcScrollTime() {
            let currentTime = DateTime.local().setZone(this._timeZone);
            return currentTime.hour > 1
                ? currentTime.minus({ hour: 1 }).toFormat("HH:mm")
                : currentTime.toFormat("HH:mm");
        },
        setResource(resource) {
            if (resource) {
                this.activeResource = [resource];
                this.selectedResource = resource;
                this.userPreferences.selectedResource = resource;
            } else {
                this.activeResource = this.resources;
                this.selectedResource = null;
            }

            this.saveUserPreferences();
        },
        async saveUserPreferences() {
            if (this._isTvView) {
                return;
            }

            await this.$store.dispatch("userPreferences/updatePref", {
                category: "calendar",
                data: this.userPreferences
            });
        },
        async getResources() {
            let response = await this.$http.get(
                process.env.BARRACUDA + "account/schedule-columns?is_settings=0"
            );

            this.resources = _.map(
                _.get(response, "data.response.resource.data", []),
                i => ({
                    id: i.type == 1 ? i.staff.id : i.id,
                    title:
                        (i.display_name == null || i.display_name == "") &&
                        i.type == 1
                            ? i.staff.firstname + " " + i.staff.lastname
                            : i.display_name,
                    initial: this.getInitial(i),
                    index: i.index,
                    type: i.type
                })
            );

            this.resources = _.sortBy(this.resources, ["index"]);

            // NOTE: Check if user has permission to view other staff calendar
            if (this._hasAccess("schedule.member.calendar")) {
                this.activeResource = _.clone(this.resources);
            } else {
                // NOTE: filter column that owned by user only
                this.activeResource = _.clone(this.resources).filter(
                    resource => resource.id == this.$root.currentActor.id
                );

                // NOTE: if zero column after filter, then remove interaction on calendar
                if (!this.activeResource.length) {
                    this.calendarPlugins = [
                        dayGridPlugin,
                        momentPlugin,
                        momentTimezonePlugin,
                        resourceTimeGridPlugin
                    ];
                }
            }
        },
        getClinicHour() {
            let self = this;
            let appointmentHour = [];
            ///appointment-hour
            return self.$http
                .get(process.env.SCHEDULE_SERVICE + "appointment-hour")
                .then(function (response) {
                    response.data.response.resources.forEach(element => {
                        if (element.status) {
                            appointmentHour.push({
                                daysOfWeek: [element.day],
                                startTime: { minutes: element.start },
                                endTime: { minutes: element.end }
                            });
                        }
                    });
                    self.config.businessHours = appointmentHour;
                });
        },
        async refetch(refresh_socket_io, appointmentId = null) {
            let self = this;
            refresh_socket_io =
                typeof refresh_socket_io === "undefined"
                    ? true
                    : refresh_socket_io;

            if (appointmentId) {
                const targetId = this.getEventId(appointmentId);
                const element = document.querySelector(`#${targetId}`);

                if (element) {
                    // VET-10601 - Disable the dragging and resizing of all events while re-render the events
                    if (!self.loadingAppointments.includes(appointmentId)) {
                        self.loadingAppointments.push(appointmentId);
                    }
                    element.classList.remove(
                        "fc-draggable", // Drag and change the appointment time/provider
                        "fc-time-grid-event" // Event to resizing the event to prolong or shorten the appointment
                    );

                    // Add spinner to show that the appointment is in loading state and cannot be dragged, resize or clicked
                    const spinner = document.createElement("i");
                    spinner.className = "fa-spin fa-spinner fas";
                    spinner.style.cssText = `
                            position: absolute;
                            bottom: 5px;
                            right: 5px;
                            font-size: 12px;
                            z-index: 10;
                        `;
                    element.appendChild(spinner);

                    self.$once("eventLoaded", () => {
                        // VET-10601 - Re-enabled the dragging and resizing of all events after re-render events
                        element.classList.add(
                            "fc-draggable",
                            "fc-time-grid-event"
                        );
                        spinner.remove();
                        const index = self.loadingAppointments.indexOf(
                            appointmentId
                        );
                        if (index !== -1) {
                            self.loadingAppointments.splice(index, 1);
                        }
                    });
                }
            }

            this.calendarApi.refetchEvents({ source: "refresh" });
            if (refresh_socket_io) {
                this.$once("eventLoaded", params => {
                    this.$socket.client.emit("v-broadcast-all", {
                        action: "refresh",
                        businessId: this.$root.setting.business_id,
                        instance: this.calendarId,
                        params
                    });
                });
            }
        },
        getInitial(column) {
            if (column.type == 1) {
                let firstname = column.staff.firstname.trim();
                let lastname = column.staff.lastname.trim();
                return firstname.substring(0, 1) + lastname.substring(0, 1);
            } else {
                return _.toUpper(column.display_name.substring(0, 2));
            }
        },
        // eslint-disable-next-line no-unused-vars
        events: _.debounce(function (info, successCallback, failureCallback) {
            let self = this;

            let params = {
                start: info.start,
                end: info.end,
                location: true,
                client: true,
                patient: true,
                staff: true
            };

            self.$http
                .get(process.env.SCHEDULE_SERVICE + "appointment", {
                    params
                })
                .then(function (response) {
                    self.popoverEvents = _.uniqBy(
                        _.cloneDeep(
                            _.get(response, "data.response.resources", []).map(
                                event => {
                                    event.borderColor =
                                        self._lightOrDark(event.color) ===
                                        "light"
                                            ? "#ababab"
                                            : event.color;
                                    return event;
                                }
                            )
                        ),
                        "id"
                    );

                    self.$emit("eventLoaded", params);
                    successCallback(self.popoverEvents);
                });
        }, 1000),
        refresh() {
            this.calendarApi.refetchEvents();
        },
        eventCreated(event) {
            /**
             * check if user has permission to create appointment
             */
            if (this._hasAccess("schedule.appointment.create")) {
                event.id = uuidv4();
                event.is_new = true;
                event.instance = this.calendarId;
                this.$refs.createUpdateWindow.open(event);

                //-- Wait for appointment create new fully loaded
                this.$root.$once("createDraft", appointment_data => {
                    this.newCreateDraft = {
                        action: "draft-add",
                        id: event.id,
                        title:
                            "Tentatively blocked by " +
                            this.$root.currentActor.firstname +
                            " " +
                            this.$root.currentActor.lastname,
                        instance: this.calendarId,
                        start: appointment_data.start,
                        businessId: this.$root.setting.business_id,
                        end: appointment_data.end,
                        resourceId: appointment_data.staff_id,
                        module: "schedule"
                    };

                    this.$socket.client.emit(
                        "v-broadcast-all",
                        this.newCreateDraft
                    );
                });
            }
        },
        eventSelected(eventClickInfo) {
            const elementId = eventClickInfo?.el?.id;
            if (elementId) {
                const appointmentId = elementId.replace("t-body-", "");
                const isAppointmentLoading = this.loadingAppointments.includes(
                    appointmentId
                );
                if (isAppointmentLoading) {
                    return;
                }
            }
            if (_.get(eventClickInfo, "event.extendedProps.status")) {
                this.$root.$emit(`bv::hide::popover`);
                this.$refs.previewWindow.open(eventClickInfo.event);
            }
        },
        eventDrop(info) {
            // add confirmation before proceed to prevent accidental drag & drop
            this.selectedEvent = info;

            if (this._isTvView) {
                // Due to fullcalendar is broken, programmatically revert changes using existing function
                this._message(
                    "Appointment cannot be updated in view-only mode",
                    "warning"
                );
                this.selectedEvent.revert();
            } else {
                this.$refs.clickDrag.open(info);
            }
        },
        eventResized(info) {
            let self = this;
            if (this._isTvView) {
                // Due to fullcalendar editable props is broken, programmatically revert changes using existing function
                this.selectedEvent = info;
                this._message(
                    "Appointment cannot be updated in view-only mode",
                    "warning"
                );
                this.selectedEvent.revert();
            } else {
                let post = {
                    duration:
                        moment(info.event.end).unix() -
                        moment(info.event.start).unix(),
                    start: moment(info.event.start).unix(),
                    timestamped: moment().unix()
                };

                return self.$http
                    .put(
                        process.env.SCHEDULE_SERVICE +
                            "appointment/update-duration/" +
                            info.event.id,
                        post
                    )
                    .then(function (response) {
                        if (response.data.response.message.code === 200) {
                            self.refetch();
                            self._message("Appointment successfully saved");
                        } else {
                            info.revert();
                            self._message(
                                "Appointment failed to save",
                                "danger"
                            );
                        }
                    })
                    .catch(function () {
                        info.revert();
                        self._message("Appointment failed to save", "danger");
                    });
            }
        },
        cancelDrop(skipRevert = false) {
            if (!skipRevert) {
                this.selectedEvent.revert();
            }
        },
        previewListener() {
            let self = this;
            return {
                edit(event, appointment_data) {
                    self.$refs.createUpdateWindow.open(appointment_data.id);
                },
                duplicate(event, appointment_data) {
                    let duplicate = _.clone(appointment_data);
                    duplicate.is_duplicate = true;
                    self.$refs.createUpdateWindow.open(duplicate);
                },
                checkin(event, appointment_data) {
                    self.$refs.checkInWindow.open(appointment_data.id);
                },
                checkout(event, appointment_data) {
                    self.$refs.checkOutWindow.open(appointment_data.id);
                },
                cancel(event, appointment_data) {
                    self.$refs.cancelWindow.open(appointment_data.id);
                },
                accept(event, appointment_data) {
                    self.$refs.acceptWindow.open(appointment_data.id);
                },
                reject(event, appointment_data) {
                    self.$refs.rejectWindow.open(appointment_data.id);
                },
                reschedule(event, appointment_data) {
                    self.$refs.rescheduleWindow.open(appointment_data.id);
                },
                undo(event, appointment_data) {
                    self.$refs.undoWindow.open(
                        appointment_data.id,
                        appointment_data.start
                    );
                },
                "history-click"(event, appointment_data) {
                    self.$refs.historyWindow.open(appointment_data);
                },
                status(event, appointment_data) {
                    self.$refs.statusWindow.open(appointment_data);
                },
                print(event, appointment_data) {
                    self.$refs.cageCardWindow.open(appointment_data);
                }
            };
        },
        eventRender(info) {
            if (_.get(info, "event.extendedProps.status") && !info.isMirror) {
                let event = info.event;
                let fcevent = null;
                if (
                    this.generatedFc[event.id] &&
                    this.generatedFc[event.id].data.extendedProps.update_at ===
                        event.extendedProps.update_at
                ) {
                    fcevent = this.generatedFc[event.id];
                } else {
                    let FcEvent = Vue.extend(fcEvent);
                    fcevent = new FcEvent({
                        propsData: { data: event, router: this.$router }
                    });

                    _.forIn(this.previewListener(), (value, key) =>
                        fcevent.$on(key, value)
                    );

                    fcevent.$parent = this.$refs["fullCalendar"];
                    fcevent.$root = this.$root;
                    fcevent.$mount();
                    this.generatedFc[event.id] = fcevent;
                }

                $(info.el)
                    .attr("id", this.getEventId(event.id))
                    .find(".fc-content")
                    .html(fcevent.$el);
            }

            if (_.get(info, "event.title")) {
                $(info.el).attr("title", _.get(info, "event.title"));
            }
        },
        eventMouseEnter(/* event */) {
            this.$root.$emit("bv::hide::popover");
            // this.$root.$emit("eventMouseEnter", event);
        },
        forceRerenderButton() {
            this.componentKey += 1;
        },
        viewRender() {
            this.$nextTick(() => {
                this.viewLoaded++;
            });

            this.forceRerenderButton();
        },
        datesRender(info) {
            if (this.calendarApi.scrollToTime) {
                this.calendarApi.scrollToTime(this.calcScrollTime());
            }
            this.calendarStart = moment(info.view.activeStart).tz(
                this.timezone
            );
            this.calendarEnd = moment(info.view.activeEnd).tz(this.timezone);
        },
        changeDayPicker(view, event, savePref = true) {
            this.calendarApi.changeView(view, event.date.toDate());

            $(".fc-state-active").removeClass("fc-state-active");

            if (view === "resourceTimeGridWeek") {
                $(".fc-weekSelector-button").addClass("fc-state-active");
            } else {
                $(".fc-dateSelector-button").addClass("fc-state-active");
            }
            $(".fc-weekSelector-button, .fc-dateSelector-button").blur();

            // user preferences object
            let viewOptions = this.userPreferences.viewOptions;
            if (view === "resourceTimeGridDay") {
                viewOptions.today = {
                    enabled: false
                };
                viewOptions.day = {
                    enabled: true,
                    date: event.date.unix()
                };
                viewOptions.week = {
                    enabled: false,
                    date: null
                };
            } else if (view === "resourceTimeGridWeek") {
                viewOptions.today = {
                    enabled: false
                };
                viewOptions.day = {
                    enabled: false,
                    date: null
                };
                viewOptions.week = {
                    enabled: true,
                    date: event.date.unix()
                };
            } else {
                viewOptions.today = {
                    enabled: true
                };
                viewOptions.day = {
                    enabled: false,
                    date: null
                };
                viewOptions.week = {
                    enabled: false,
                    date: null
                };
            }

            if (savePref) {
                this.saveUserPreferences();
            }
        },
        hideModal() {
            this.$refs.myModalRef.hide();
        }
    }
};
</script>
<style lang="scss">
@import "~@fullcalendar/core/main.css";
@import "~@fullcalendar/timegrid/main.css";

#fc-schedule-appointment {
    .temporary-block {
        background: rgb(255, 255, 255);
        background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(240, 240, 240, 1) 50%,
            rgba(0, 0, 0, 0) 100%
        );
        height: 10rem;
    }

    .fc-view {
        overflow: hidden;
        width: auto;
    }

    .fc-business-container .fc-nonbusiness {
        background-color: #666666;
    }

    .staff-detail p {
        display: inline-block;
    }

    .fc-right div.fc-button-group {
        margin-left: 30px;
        font-family: Verdana, Helvetica, Arial, sans-serif;
    }
    .fc-right .fc-button-group:nth-child(1) button.fc-button {
        width: 62px;
        font-size: 12px;
        font-family: Verdana, Helvetica, Arial, sans-serif;
    }
    .fc .nav > li {
        height: 15px;
    }

    .fc .nav-pills > li > a {
        margin: 0;
        padding: 0;
    }
    .fc .nav-tabs .open .dropdown-toggle,
    .nav-pills .open .dropdown-toggle,
    .nav > li.dropdown.open.active > a:hover {
        background: none;
    }

    .fc-unthemed td.fc-today {
        background: none;
    }

    .fc .nav > li > a.hover {
        background-color: none;
    }
    .fc-view {
        background-color: white;
    }

    .fc-event .fc-content {
        font-weight: normal;
        color: black;
        background-color: #ffffff90;
    }

    .fc-content {
        padding-left: 1%;
        padding-right: 1%;
        font-size: 0.75rem;
    }

    .fc-event {
        border-width: 1px;
        margin-right: 5px;
        border-radius: 0px;
    }

    .fc-time-grid-event.fc-short .fc-title {
        font-size: 0.7rem;
    }

    .fc-pending {
        background-image: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.25) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.25) 50%,
            rgba(255, 255, 255, 0.25) 75%,
            transparent 75%,
            transparent
        );
        background-size: 1rem 1rem;
    }
    .vh-100 {
        min-height: 80vh;
    }
    .fc-resource-cell {
        text-overflow: ellipsis;
        overflow: auto;
    }
    #calendar .fc-left {
        h2 {
            display: inline;
            font-size: 2.3333em;
            margin: 0;
        }
    }
    .fc-day-header.fc-widget-header {
        overflow: hidden;
    }

    #resource-container {
        overflow: hidden;
        .slide-leave-active,
        .slide-enter-active {
            transition: width 0.3s;
        }
        .slide-enter {
            transform: translate(0%, 0);
        }
        .slide-leave-to {
            transform: translate(-100%, 0);
            width: 0;
            display: none;
            opacity: 0;
        }
        .list-group-item {
            cursor: pointer;
        }
    }
    .calendar-container {
        .button-nav > button {
            width: 3.5rem;
            padding: 0.25rem 0.1rem;
            height: 2rem;
        }
        .button-arrow > button {
            font-size: 1.5em;
            padding: 0.23rem;
            padding-top: 0.18rem;
            width: 2.1rem;
            height: 2rem;
        }
    }
    #resource-list {
        height: min-content;
    }
}
</style>
